const STORAGE_KEY = 'farfar-message-outbox';

export type OutboxReplySnapshot = {
  id: string | number;
  sender?: string;
  text?: string;
};

export type OutboxStatus = 'queued' | 'failed';

export type OutboxEntry = {
  text: string;
  replyTo: OutboxReplySnapshot | null;
  clientMessageId: string;
  fingerprint: string;
  updatedAt: string;
  status: OutboxStatus;
};

type OutboxRoot = Record<string, Record<string, OutboxEntry>>;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  if (typeof localStorage !== 'undefined') return localStorage;
  return null;
}

function readRoot(storage: StorageLike | null): OutboxRoot {
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeRoot(storage: StorageLike | null, root: OutboxRoot): void {
  if (!storage) return;
  try {
    if (Object.keys(root).length === 0) {
      storage.removeItem(STORAGE_KEY);
      return;
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(root));
  } catch {
    // ignore quota / private-mode errors
  }
}

function normalizeEntry(entry: unknown): OutboxEntry | null {
  if (!entry || typeof entry !== 'object') return null;
  const candidate = entry as Partial<OutboxEntry>;
  if (typeof candidate.text !== 'string') return null;
  if (typeof candidate.clientMessageId !== 'string') return null;
  if (typeof candidate.fingerprint !== 'string') return null;
  if (candidate.status !== 'queued' && candidate.status !== 'failed') return null;
  return {
    text: candidate.text,
    replyTo: candidate.replyTo && typeof candidate.replyTo === 'object' && candidate.replyTo.id != null
      ? {
        id: candidate.replyTo.id,
        sender: candidate.replyTo.sender,
        text: candidate.replyTo.text
      }
      : null,
    clientMessageId: candidate.clientMessageId,
    fingerprint: candidate.fingerprint,
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : new Date(0).toISOString(),
    status: candidate.status
  };
}

export function readOutboxEntry(
  userId: string | number,
  threadId: string | number,
  storage: StorageLike | null = defaultStorage()
): OutboxEntry | null {
  const root = readRoot(storage);
  return normalizeEntry(root[String(userId)]?.[String(threadId)]) || null;
}

export function upsertOutboxEntry(
  userId: string | number,
  threadId: string | number,
  partial: {
    text: string;
    replyTo: OutboxReplySnapshot | null;
    clientMessageId: string;
    fingerprint: string;
    status?: OutboxStatus;
  },
  storage: StorageLike | null = defaultStorage()
): OutboxEntry {
  const root = readRoot(storage);
  const userKey = String(userId);
  const threadKey = String(threadId);
  const entry: OutboxEntry = {
    text: partial.text,
    replyTo: partial.replyTo,
    clientMessageId: partial.clientMessageId,
    fingerprint: partial.fingerprint,
    updatedAt: new Date().toISOString(),
    status: partial.status || 'queued'
  };
  root[userKey] = { ...(root[userKey] || {}), [threadKey]: entry };
  writeRoot(storage, root);
  return entry;
}

export function markOutboxFailed(
  userId: string | number,
  threadId: string | number,
  storage: StorageLike | null = defaultStorage()
): OutboxEntry | null {
  const root = readRoot(storage);
  const userKey = String(userId);
  const threadKey = String(threadId);
  const existing = normalizeEntry(root[userKey]?.[threadKey]);
  if (!existing) return null;
  const entry: OutboxEntry = {
    ...existing,
    status: 'failed',
    updatedAt: new Date().toISOString()
  };
  root[userKey] = { ...(root[userKey] || {}), [threadKey]: entry };
  writeRoot(storage, root);
  return entry;
}

export function removeOutboxEntry(
  userId: string | number,
  threadId: string | number,
  storage: StorageLike | null = defaultStorage()
): void {
  const root = readRoot(storage);
  const userKey = String(userId);
  const threadKey = String(threadId);
  const userEntries = root[userKey];
  if (!userEntries?.[threadKey]) return;
  const nextUserEntries = { ...userEntries };
  delete nextUserEntries[threadKey];
  if (Object.keys(nextUserEntries).length === 0) {
    delete root[userKey];
  } else {
    root[userKey] = nextUserEntries;
  }
  writeRoot(storage, root);
}

export function listOutboxForUser(
  userId: string | number,
  storage: StorageLike | null = defaultStorage()
): Array<{ threadId: string; entry: OutboxEntry }> {
  const root = readRoot(storage);
  const userEntries = root[String(userId)];
  if (!userEntries || typeof userEntries !== 'object') return [];
  return Object.entries(userEntries)
    .map(([threadId, raw]) => {
      const entry = normalizeEntry(raw);
      return entry ? { threadId, entry } : null;
    })
    .filter((item): item is { threadId: string; entry: OutboxEntry } => item != null);
}
